// SETUP VALIDATION (CR-20260910-001 milestone 4, feature request section 4.1 "Validation").
//
// Pure — no Angular, no DOM — so every rule is unit-tested directly and the same function gates
// the Apply button and produces the messages beside the controls.
//
// WHAT IT REFUSES, and nothing else (the list is the feature request's, not an invented one):
//   the hierarchy incomplete  ·  no template  ·  the range wider than the cap  ·  no hedge side
//   filled  ·  a Range coverage end strike at or inside its start.
//
// EVERY PROBLEM CARRIES A PLACE. The same reasoning as the wire refusals: a message at the Apply
// bar about a control three sections up makes the trader hunt. `placement` reuses the refusal
// placement vocabulary, so a locally-refused Setup and a backend-refused run render identically —
// the trader should not be able to tell which one caught him.
//
// THE CAP RULE IS NOT RE-IMPLEMENTED. `exceedsRangeCap` and `rangeCapMessage` are imported from
// the APG ORION questionnaire, pure functions, no module change. A second implementation of "how
// wide is too wide" is a second definition of it, and the two would drift.
//
// ⚠ ONE RULE IS DELIBERATELY NOT APPLIED HERE: the APG's own `isRollComplete` also refuses a date
// block whose directional bias is switched on without a direction. In Maven the bias is a VISIBLE,
// INERT placeholder (S10) that reaches nothing on the wire, so gating Apply on it would stop the
// trader on a control that does nothing — and the feature request's validation list does not
// mention it. The date rules that DO bind are taken from the APG unchanged.
//
// ES5 note: arrays + indexOf only.

import {RollPositionsInputs} from '../../adjustment-pricing-grid/copilot/apg-copilot-domain';
import {exceedsRangeCap, rangeCapMessage} from '../../adjustment-pricing-grid/copilot/apg-copilot-questionnaire';
import {MavenRefusalPlacement, NO_ZONE_MESSAGE} from '../run-client/maven-refusal-messages';
import {mavenRangeMode, MavenSetupInputs, MavenSideInputs} from '../maven-domain';
import {isOverrideComplete} from '../../copilot-shared/opg-copilot-domain';
import {isFiniteMavenNumber} from '../templates/maven-template-validation';

export type MavenSetupProblemCode =
    | 'noPortfolio'
    | 'noZone'
    | 'noTemplate'
    | 'noDate'
    | 'rangeExceedsCap'
    | 'noHedgeSide'
    | 'sideIncomplete'
    // CR-20260915-001: an edited override date missing a value the run consumes.
    | 'overridesIncomplete'
    // CR-20260916-003: Close with no expiration toggled (owner ruling 3).
    | 'closeNoExpiration'
    // CR-20260916-003: an expiration toggled on with no hedge selected in it.
    | 'closeExpirationEmpty';

// CR-20260916-003. The two Close-selection sentences, said before Apply; the backend's
// CloseSelectionEmpty backstop says the first of them again (maven-refusal-messages.ts).
export const CLOSE_NO_EXPIRATION_MESSAGE =
    'Close is selected but no expiration is chosen. Choose an expiration or switch to Ignore.';

export function closeExpirationEmptyMessage(dates: string[]): string {
    return 'You selected ' + dates.join(', ') + ' but no hedges to close. Deselect the expiration or choose hedges to close.';
}

// CR-20260915-001. Said here before Apply and, word for word, by the backend refusal
// MalformedMarketOverrides (maven-refusal-messages.ts) — the trader cannot tell which tier
// caught him, and should not have to.
export const OVERRIDES_INCOMPLETE_MESSAGE =
    'An overridden date is incomplete. Enter IV and the vertical skew of every enabled side, all greater than zero.';

export interface MavenSetupProblem {
    code: MavenSetupProblemCode;
    placement: MavenRefusalPlacement;
    message: string;
    side: 'calls' | 'puts' | null;
}

// What the caller knows that the Setup object does not.
export interface MavenSetupLimits {
    // CR-20260916-003: which hedges each book expiration holds, 'yyyy-MM-dd' -> group ids, as the
    // Close selection component built its list (non-expired hedges only). The validator needs it
    // to say whether a toggled expiration has any selected hedge; the Setup object holds only the
    // ids. Null = the list is not built (Ignore, or no portfolio), and no per-expiration check
    // runs. The date LABEL for the sentence is the second map.
    closeHedgesByExpiration?: { [date: string]: string[] } | null;
    closeExpirationLabels?: { [date: string]: string } | null;
    // The shell-level "Max Query Range (days)" ORION setting. null = unknown, which means
    // UNLIMITED — and only until the setting arrives. It is never a licence.
    maxRangeDays: number | null;
    // The selected portfolio's CURRENT ZONE, as the Setup's Current Zone line shows it (vrequest
    // 2026-09-14, replacing ruling Q4's "the footer shows it"). null = N/A: no portfolio, no zone
    // picked for it yet, or the read still in flight. A run without a zone is refused by the
    // backend ("Maven cannot run without a zone", contract M1); this refuses it BEFORE Apply, in
    // the same words. The zone itself never crosses the wire from here — the backend reads its own.
    currentZone: number | null;
}

export function noLimits(): MavenSetupLimits {
    return {maxRangeDays: null, currentZone: null};
}

function hasDate(dates: RollPositionsInputs): boolean {
    if (!dates || !dates.date) {
        return false;
    }
    const sel: any = dates.date;
    if (sel.kind === 'single') {
        return isRealDate(sel.single);
    }
    // A range needs BOTH ends, and the start strictly before the end — the same rule the APG
    // questionnaire applies, and the same rule its End picker enforces by being forward-only.
    return isRealDate(sel.start) && isRealDate(sel.end) && sel.start.getTime() < sel.end.getTime();
}

function isRealDate(d: any): boolean {
    return d instanceof Date && !isNaN(d.getTime());
}

function isFiniteNumber(n: any): boolean {
    return isFiniteMavenNumber(n);
}

// A side the trader OPENED must be answered. A side he never opened is not a problem at all —
// that distinction is why `enabled` exists rather than being inferred from the fields.
function sideProblems(
    side: MavenSideInputs,
    which: 'calls' | 'puts'
): MavenSetupProblem[] {
    const out: MavenSetupProblem[] = [];
    if (!side || !side.enabled) {
        return out;
    }

    const placement: MavenRefusalPlacement = which === 'calls' ? 'callsCoverage' : 'putsCoverage';

    if (side.mode && side.mode !== 'custom' && side.mode !== 'preset') {
        out.push({code: 'sideIncomplete', placement, side: which, message: 'Saved Hedge Mode Is Unsupported.'});
        return out;
    }

    if (side.mode === 'preset') {
        if (!side.presetId || side.presetValid !== true) {
            out.push({code: 'sideIncomplete', placement, side: which, message: 'Select an Available Hedging Preset.'});
        }
        return out;
    }

    const offsetMissing = !isFiniteNumber(side.offsetFromShorts);
    const minProfitMissing = !isFiniteNumber(side.minProfit ? side.minProfit.amount : null)
        || (side.minProfit.amount as number) <= 0;
    const riskMissing = !side.maxRisk
        || (side.maxRisk.kind === 'dollar' && !(isFiniteNumber(side.maxRisk.amount) && (side.maxRisk.amount as number) > 0))
        || (side.maxRisk.kind === 'relative' && !side.maxRisk.level);
    const coverageMissing = !side.coverage
        || (side.coverage.kind === 'range' && (mavenRangeMode(side.coverage) === 'distance'
            ? !isFiniteNumber(side.coverage.rangeSize) || side.coverage.rangeSize <= 0
            : mavenRangeMode(side.coverage) !== 'endStrike' || !isFiniteNumber(side.coverage.endStrike)));

    if (offsetMissing || minProfitMissing || riskMissing || coverageMissing) {
        out.push({
            code: 'sideIncomplete',
            placement: placement,
            message: which === 'calls'
                ? 'The calls side is open but not finished. Fill in the offset, the coverage, the minimum profit and the risk.'
                : 'The puts side is open but not finished. Fill in the offset, the coverage, the minimum profit and the risk.',
            side: which
        });
        return out;
    }

    // ⚠ R-H (an end strike at or inside its start) IS NOT CHECKED HERE, and that is a ruling
    // (leader L11), not a gap. The rule is enforced by the BACKEND validator, before anything is
    // ordered, because only the backend holds what the rule needs: the saved positions and the
    // zone. The anchors are knowable pre-send — #3 keeps today's short strikes, #2B lands on the
    // zone — but they are knowable THERE, not here: this panel sends ids only and holds neither.
    //
    // "Refused in the Setup" names where the trader SEES it, not which tier computes it. The
    // backend answers `preSendBlocked` with `EndStrikeInsideStart` and the Setup renders it beside
    // this very control, exactly as it renders `NoZone`. Do not add a local copy: a second author
    // of the rule, working from an anchor it had to guess at, is how the two answers start to
    // disagree.
    return out;
}

// Every problem with this Setup, in HIERARCHY ORDER — portfolio, template, dates, hedges, hedge
// parameters. Order matters to the caller: the first problem is the one to lead with, because a
// value further down is meaningless without the ones above it.
export function validateMavenSetup(setup: MavenSetupInputs, limits: MavenSetupLimits): MavenSetupProblem[] {
    const problems: MavenSetupProblem[] = [];
    const lim = limits || noLimits();

    if (!setup) {
        return [{code: 'noPortfolio', placement: 'portfolio', message: 'Choose a portfolio first.', side: null}];
    }

    if (!setup.portfolioId) {
        problems.push({
            code: 'noPortfolio',
            placement: 'portfolio',
            message: 'Choose a portfolio first.',
            side: null
        });
    } else if (!isFiniteNumber(lim.currentZone)) {
        // Only once a portfolio is chosen: without one, "no zone" says nothing the line above
        // does not. The zone is set in the footer indicator's pop-up, never here (no override).
        problems.push({
            code: 'noZone',
            placement: 'portfolio',
            message: NO_ZONE_MESSAGE,
            side: null
        });
    }

    if (!setup.templateId) {
        problems.push({
            code: 'noTemplate',
            placement: 'template',
            message: 'Choose an adjustment template.',
            side: null
        });
    }

    if (!hasDate(setup.dates)) {
        problems.push({
            code: 'noDate',
            placement: 'dates',
            message: 'Choose a date, or a date range.',
            side: null
        });
    } else if (exceedsRangeCap(setup.dates, {maxRangeDays: lim.maxRangeDays})) {
        problems.push({
            code: 'rangeExceedsCap',
            placement: 'dates',
            // The APG's own sentence, imported rather than re-typed: the trader meets the same
            // limit in two panels and should meet the same words.
            message: rangeCapMessage(lim.maxRangeDays as number),
            side: null
        });
    }

    // CR-20260916-003: the Close selection (owner rulings 3 and the sketch's error rule). Sits
    // between the dates and the hedge sides in hierarchy order, where the section sits.
    const closeProblem = closeSelectionProblem(setup, lim);
    if (closeProblem) {
        problems.push(closeProblem);
    }

    const callsOn = !!(setup.calls && setup.calls.enabled);
    const putsOn = !!(setup.puts && setup.puts.enabled);

    if (!callsOn && !putsOn) {
        problems.push({
            code: 'noHedgeSide',
            placement: 'hedgeSides',
            message: 'Fill in the calls, the puts, or both. One side is enough; neither is not.',
            side: null
        });
    }

    const callsProblems = sideProblems(setup.calls, 'calls');
    for (let i = 0; i < callsProblems.length; i++) {
        problems.push(callsProblems[i]);
    }
    const putsProblems = sideProblems(setup.puts, 'puts');
    for (let i = 0; i < putsProblems.length; i++) {
        problems.push(putsProblems[i]);
    }

    // CR-20260915-001: an EDITED override date must carry what the run consumes — the ORION
    // (OPG)'s own consume-scoped rule (IV always; a side's vertical skew iff that side runs). One
    // problem for the whole map: the offending boxes are already red in the block itself, so the
    // sentence only has to say why Apply is off. HS is never required here (owner ruling D4:
    // carried, never consumed). The map holds edited dates only, so nothing else needs filtering.
    if (hasIncompleteOverride(setup, callsOn, putsOn)) {
        problems.push({
            code: 'overridesIncomplete',
            placement: 'marketOverrides',
            message: OVERRIDES_INCOMPLETE_MESSAGE,
            side: null
        });
    }

    return problems;
}

// CR-20260916-003. Under Close: no expiration toggled is one problem; a toggled expiration with
// none of ITS hedges selected is the other (one sentence naming every such date). A hedge counts
// for an expiration when the list built for that expiration holds its id — a calendar hedge
// therefore satisfies every expiration it spans (ruling 9). Under Ignore, nothing.
function closeSelectionProblem(setup: MavenSetupInputs, lim: MavenSetupLimits): MavenSetupProblem | null {
    if (setup.existingHedges !== 'close') {
        return null;
    }
    const sel = setup.closeSelection || {expirations: [], hedgeIds: []};
    const expirations = sel.expirations || [];
    if (expirations.length === 0) {
        return {code: 'closeNoExpiration', placement: 'existingHedges', message: CLOSE_NO_EXPIRATION_MESSAGE, side: null};
    }
    const byExp = lim.closeHedgesByExpiration;
    if (!byExp) {
        return null;
    }
    const selected = sel.hedgeIds || [];
    const empty: string[] = [];
    for (let i = 0; i < expirations.length; i++) {
        const ids = byExp[expirations[i]] || [];
        let any = false;
        for (let j = 0; j < ids.length; j++) {
            if (selected.indexOf(ids[j]) >= 0) {
                any = true;
                break;
            }
        }
        if (!any) {
            const labels = lim.closeExpirationLabels || {};
            empty.push(labels[expirations[i]] || expirations[i]);
        }
    }
    if (empty.length === 0) {
        return null;
    }
    return {code: 'closeExpirationEmpty', placement: 'existingHedges', message: closeExpirationEmptyMessage(empty), side: null};
}

function hasIncompleteOverride(setup: MavenSetupInputs, callsOn: boolean, putsOn: boolean): boolean {
    callsOn = callsOn && setup.calls.mode !== 'preset';
    putsOn = putsOn && setup.puts.mode !== 'preset';
    if (!callsOn && !putsOn) { return false; }
    const map = setup.marketOverrides;
    if (!map) {
        return false;
    }
    const keys = Object.keys(map);
    for (let i = 0; i < keys.length; i++) {
        const record = map[keys[i]];
        if (record && record.edited && !isOverrideComplete(record, callsOn, putsOn)) {
            return true;
        }
    }
    return false;
}

export function canApply(setup: MavenSetupInputs, limits: MavenSetupLimits): boolean {
    return validateMavenSetup(setup, limits).length === 0;
}

// The problems that belong beside ONE control, for rendering in place.
export function problemsAt(
    problems: MavenSetupProblem[],
    placement: MavenRefusalPlacement
): MavenSetupProblem[] {
    return (problems || []).filter(p => p.placement === placement);
}

// ---- Section completeness (the hierarchy hint, NOT a lock) ----------------------------------
//
// The feature request leaves "whether lower sections are locked until upper ones are complete" as
// a UI judgement call to make when building. Taken as: a HINT, never a lock. A locked section
// hides the reason it is locked, and Apply already refuses an incomplete hierarchy with a message
// that says which control is at fault — so locking buys nothing and costs the trader the ability
// to fill the Setup in whatever order he thinks in.

export type MavenSetupSection = 'portfolio' | 'dates' | 'existingHedges' | 'hedgeParameters';

export function isSectionComplete(
    setup: MavenSetupInputs,
    section: MavenSetupSection,
    limits: MavenSetupLimits
): boolean {
    const lim = limits || noLimits();
    if (!setup) {
        return false;
    }
    switch (section) {
        case 'portfolio':
            return !!setup.portfolioId && !!setup.templateId;
        case 'dates':
            return hasDate(setup.dates) && !exceedsRangeCap(setup.dates, {maxRangeDays: lim.maxRangeDays});
        case 'existingHedges':
            // Ignore is always complete (it is where it starts). Close is complete once its
            // selection passes (CR-20260916-003).
            if (setup.existingHedges === 'ignore') {
                return true;
            }
            return setup.existingHedges === 'close' && closeSelectionProblem(setup, lim) === null;
        case 'hedgeParameters':
            return canSidesRun(setup);
        default:
            return false;
    }
}

function canSidesRun(setup: MavenSetupInputs): boolean {
    const callsOn = !!(setup.calls && setup.calls.enabled);
    const putsOn = !!(setup.puts && setup.puts.enabled);
    if (!callsOn && !putsOn) {
        return false;
    }
    return sideProblems(setup.calls, 'calls').length === 0
        && sideProblems(setup.puts, 'puts').length === 0;
}

// The section the panel should highlight as "where you are": the first one that is not finished,
// or the last one once everything is.
export function activeSection(setup: MavenSetupInputs, limits: MavenSetupLimits): MavenSetupSection {
    const order: MavenSetupSection[] = ['portfolio', 'dates', 'existingHedges', 'hedgeParameters'];
    for (let i = 0; i < order.length; i++) {
        if (!isSectionComplete(setup, order[i], limits)) {
            return order[i];
        }
    }
    return 'hedgeParameters';
}
